import { c as _c } from "react/compiler-runtime";
import { useLayoutEffect, useState } from "react";

/**
 * ZoomInfo WebSights company-in-session bridge.
 *
 * When "Website Personalization" is enabled in the ZoomInfo portal, the
 * WebSights script (a GTM-managed tag, so it only loads after MARKETING
 * consent) calls `window._zi_ws.wsVisitorInfo(visitorInfo)` with the company
 * that owns the visitor's IP address. This module defines that callback and
 * forwards a small, allow-listed company record to Data Cloud as ONE custom
 * `ziCompanyIdentified` event per page load.
 *
 * Behaviour guarantees:
 *  - Off unless NEXT_PUBLIC_ZI_COMPANY_EVENT is "1"/"true". Off means nothing
 *    is defined on `window` and the page behaves exactly as before.
 *  - Installed only while marketing consent is granted, and it must be
 *    installed BEFORE the GTM init script is injected (see
 *    `useZiCompanyBridge`). Withdrawing consent restores the page's own
 *    callback and drops any queued payload.
 *  - Only the allow-listed fields are ever read out of the visitor info. The
 *    visitor's IP address, address, ticker, funding, NAICS/SIC codes and
 *    everything else ZoomInfo returns is dropped. The raw object is never
 *    stored or logged.
 *  - The event is sent only once the Salesforce SDK has finished `init()` and
 *    marketing consent is still granted; before that one payload is queued.
 *  - An existing `wsVisitorInfo` callback is wrapped, never overwritten, and a
 *    callback the page assigns later (for example from a GTM Custom HTML tag)
 *    is wrapped too.
 *  - Nothing here throws into the host page.
 *
 * ZoomInfo's payload carries no ZoomInfo company id, so the `website` domain
 * is the join key to Account downstream. `_ziVisitorInfo` in localStorage is
 * deliberately not read.
 */

export const ZI_COMPANY_EVENT_NAME = "ziCompanyIdentified";
const MAX_TEXT_LENGTH = 255;
type WsVisitorInfoCallback = (this: unknown, ...args: unknown[]) => unknown;
interface ZiWebSights {
  wsVisitorInfo?: WsVisitorInfoCallback;
  [key: string]: unknown;
}
declare global {
  interface Window {
    _zi_ws?: ZiWebSights;
  }
}

/** The only company attributes that ever leave the browser. */
export interface ZiCompanyPayload {
  companyName?: string;
  /** Domain only: no scheme, path, port or leading "www.". The join key to Account. */
  website?: string;
  /** ZoomInfo `primaryIndustry`. */
  industry?: string;
  employeeCount?: number;
  revenue?: number;
}

// Event shape for the Data Cloud custom event. Kept here rather than widening
// the shared SalesforceInteractionEvent type: custom attributes sit flat on
// `interaction` (see the Interactions SDK "Custom Events" guide).
interface ZiCompanyEvent {
  interaction: ZiCompanyPayload & {
    name: string;
    eventType: string;
  };
}
export function isZiCompanyEventEnabled(): boolean {
  const flag = process.env.NEXT_PUBLIC_ZI_COMPANY_EVENT?.trim().toLowerCase();
  return flag === "1" || flag === "true";
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function cleanText(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  // Drop control and invisible format characters (zero-width, bidi
  // overrides), collapse whitespace, then cap by code point so a surrogate
  // pair is never cut in half.
  const text = Array.from(value.replace(/[\p{Cc}\p{Cf}]/gu, " ").replace(/\s+/g, " ").trim()).slice(0, MAX_TEXT_LENGTH).join("").trim();
  return text.length > 0 ? text : undefined;
}

// ZoomInfo reports 0 for firmographics it does not know, so a count must be
// positive to be worth sending.
function cleanCount(value: unknown): number | undefined {
  let count: number | undefined;
  if (typeof value === "number") {
    count = value;
  } else if (typeof value === "string" && /^\d+(\.\d+)?$/.test(value.trim())) {
    count = Number(value.trim());
  }
  return count !== undefined && Number.isFinite(count) && count > 0 ? count : undefined;
}

// The TLD must be letters (or punycode), which is what keeps IPv4 literals
// such as 203.0.113.9 out of the `website` field.
const DOMAIN_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:[a-z]{2,}|xn--[a-z0-9-]+)$/;

/** Reduce a URL-ish string to its bare domain, or undefined if it is not one. */
export function domainOnly(value: unknown): string | undefined {
  const raw = cleanText(value);
  if (!raw) return undefined;
  let hostname: string;
  try {
    hostname = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `http://${raw}`).hostname;
  } catch {
    return undefined;
  }
  const domain = hostname.toLowerCase().replace(/\.$/, "").replace(/^www\./, "");
  return DOMAIN_PATTERN.test(domain) ? domain : undefined;
}

/**
 * Allow-list a ZoomInfo visitor-info object. Accepts both the documented
 * `{ status, ziDetails: {...} }` shape and a flat `{...}` shape. Returns null
 * when ZoomInfo did not identify a company (IP-only response, error status,
 * encrypted `ziDetails` string, or unrecognised input).
 */
export function extractZiCompany(visitorInfo: unknown): ZiCompanyPayload | null {
  if (!isRecord(visitorInfo)) return null;
  if (visitorInfo.status !== undefined && visitorInfo.status !== "success") return null;
  let details: Record<string, unknown>;
  if (visitorInfo.ziDetails === undefined) {
    details = visitorInfo;
  } else if (isRecord(visitorInfo.ziDetails)) {
    details = visitorInfo.ziDetails;
  } else {
    return null;
  }
  const payload: ZiCompanyPayload = {};
  const companyName = cleanText(details.companyName);
  const website = domainOnly(details.website);
  const industry = cleanText(details.primaryIndustry);
  const employeeCount = cleanCount(details.employeeCount);
  const revenue = cleanCount(details.revenue);
  if (companyName !== undefined) payload.companyName = companyName;
  if (website !== undefined) payload.website = website;
  if (industry !== undefined) payload.industry = industry;
  if (employeeCount !== undefined) payload.employeeCount = employeeCount;
  if (revenue !== undefined) payload.revenue = revenue;

  // An IP-only match has nothing to report. Require a name or a domain.
  return payload.companyName !== undefined || payload.website !== undefined ? payload : null;
}
function sendCompanyEvent(sdk: NonNullable<Window["SalesforceInteractions"]>, payload: ZiCompanyPayload): void {
  const event: ZiCompanyEvent = {
    interaction: {
      name: ZI_COMPANY_EVENT_NAME,
      eventType: ZI_COMPANY_EVENT_NAME,
      ...payload
    }
  };
  sdk.sendEvent(event);
}
export interface ZiCompanyBridge {
  /** Define `window._zi_ws.wsVisitorInfo`, wrapping any existing callback. */
  install(): void;
  /** Restore the page's own callback and drop any queued payload. */
  uninstall(): void;
  /** The Salesforce SDK finished `init()`; flush the queued payload. */
  markSdkReady(): void;
}
export function createZiCompanyBridge(): ZiCompanyBridge {
  // The callback we installed, and the object we installed it on. `wrapper`
  // is non-null exactly while marketing consent is granted: the hook
  // installs on grant and uninstalls on withdrawal, so it doubles as the
  // "consent still granted" check at queue and flush time.
  let wrapper: WsVisitorInfoCallback | null = null;
  let target: ZiWebSights | null = null;
  let createdTarget = false;
  let restoreHost: () => WsVisitorInfoCallback | undefined = () => undefined;
  let sdkReady = false;
  let sent = false;
  let pending: ZiCompanyPayload | null = null;
  const flush = (): void => {
    if (!wrapper || !sdkReady || sent || !pending) return;
    const sdk = window.SalesforceInteractions;
    if (!sdk) return;
    const payload = pending;
    // At most once per page load: spent before the send so that an SDK
    // that throws part-way can never produce a duplicate.
    pending = null;
    sent = true;
    try {
      sendCompanyEvent(sdk, payload);
    } catch {
      // Never throw into the host page.
    }
  };
  const queue = (visitorInfo: unknown): void => {
    if (!wrapper || sent || pending) return;
    const payload = extractZiCompany(visitorInfo);
    if (payload) {
      pending = payload;
      flush();
    }
  };
  return {
    install() {
      if (typeof window === "undefined" || wrapper) return;
      try {
        createdTarget = window._zi_ws === undefined;
        const zi: ZiWebSights = window._zi_ws ?? {};
        // The page's own callback. A later `wsVisitorInfo = fn`
        // assignment lands here too through the setter below.
        let host = typeof zi.wsVisitorInfo === "function" ? zi.wsVisitorInfo : undefined;
        const installed: WsVisitorInfoCallback = function (this: unknown, ...args: unknown[]) {
          try {
            queue(args[0]);
          } catch {
            // Never throw into the host page.
          }
          // The page's own callback is the host's business: it runs
          // exactly as it did before, with the original arguments.
          return host?.apply(this, args);
        };
        Object.defineProperty(zi, "wsVisitorInfo", {
          configurable: true,
          enumerable: true,
          get: () => installed,
          set: (next: unknown) => {
            host = typeof next === "function" ? next as WsVisitorInfoCallback : undefined;
          }
        });
        window._zi_ws = zi;
        wrapper = installed;
        target = zi;
        restoreHost = () => host;
      } catch {
        // A frozen or otherwise unusable `_zi_ws`: fail closed, and
        // never throw into the host page.
        wrapper = null;
        target = null;
      }
    },
    uninstall() {
      pending = null;
      const zi = target;
      const installed = wrapper;
      wrapper = null;
      target = null;
      if (!zi || !installed) return;
      try {
        if (zi.wsVisitorInfo !== installed) return;
        const host = restoreHost();
        delete zi.wsVisitorInfo;
        if (host) {
          zi.wsVisitorInfo = host;
        } else if (createdTarget && window._zi_ws === zi && Object.keys(zi).length === 0) {
          delete window._zi_ws;
        }
      } catch {
        // Never throw into the host page.
      }
    },
    markSdkReady() {
      sdkReady = true;
      flush();
    }
  };
}

/**
 * Wires the bridge to marketing consent. Call it from ThirdPartyScripts
 * BEFORE the GTM init script renders.
 *
 * The callback is defined in a layout effect on purpose: React runs every
 * layout effect of a commit before any passive effect of that commit, and
 * next/script starts loading the GTM tag from a passive effect (then defers it
 * further to window load + idle). So `wsVisitorInfo` is always in place before
 * GTM, and therefore the ZoomInfo tag, can run. A passive effect in the parent
 * would run after the child <Script>'s own effect.
 * ThirdPartyScripts.ziCompany.test.tsx fails if this is changed to useEffect.
 */
export function useZiCompanyBridge(active) {
  const $ = _c(5);
  const [bridge] = useState(createZiCompanyBridge);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = isZiCompanyEventEnabled();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const enabled = t0;
  let t1;
  let t2;
  if ($[1] !== active || $[2] !== bridge) {
    t1 = () => {
      if (!enabled || !active) {
        return;
      }
      bridge.install();
      return () => bridge.uninstall();
    };
    t2 = [bridge, enabled, active];
    $[1] = active;
    $[2] = bridge;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useLayoutEffect(t1, t2);
  return bridge;
}